/**
 * The 401 detail the API's auth middleware returns when the Bearer token (or
 * session cookie) fails verification, for example because the Clerk session JWT
 * has expired. This is the first middleware to run, so a request rejected with
 * this detail never reached a handler and is safe to replay.
 *
 * Must match the string raised in apps/mcv-api/mcv_api/middleware.py
 * (NotAuthorizedException). Handler and guard 401s use other details and are
 * deliberately not matched.
 *
 * Leaf module (no imports) so features/utils.ts can use it without importing
 * utils/service, which many specs mock with a factory.
 */
export const AUTH_REJECTED_DETAIL = 'Requestor is not an authenticated user';

export const SESSION_REFRESH_TEXT = 'Session needs to refresh';
export const SESSION_REFRESH_SUBTEXT = 'Please refresh the page to continue.';

const hasAuthRejectedDetail = (body: unknown): boolean =>
  body != null &&
  typeof body === 'object' &&
  (body as { detail?: unknown }).detail === AUTH_REJECTED_DETAIL;

/**
 * True when `error` is an HTTP 401 whose body carries the auth middleware's
 * rejection detail. Duck-typed so it works for any axios-shaped error. Reads an
 * already-parsed body only: a Blob or ArrayBuffer body needs
 * `isMiddlewareAuthRejectionBinaryBody`.
 */
export const isMiddlewareAuthRejection = (error: unknown): boolean => {
  if (error == null || typeof error !== 'object') return false;
  const response = (error as { response?: unknown }).response;
  if (response == null || typeof response !== 'object') return false;
  const { status, data } = response as { status?: unknown; data?: unknown };
  if (status !== 401) return false;
  return hasAuthRejectedDetail(data);
};

// Object.prototype.toString tags rather than instanceof, so a body built in
// another realm (an iframe, a worker, a test environment) is still recognised.
const typeTag = (value: unknown): string => Object.prototype.toString.call(value);
const isBlob = (value: unknown): value is Blob => typeTag(value) === '[object Blob]';
const isArrayBuffer = (value: unknown): value is ArrayBuffer =>
  typeTag(value) === '[object ArrayBuffer]';

// Blob.text() does not consume the Blob, so the error body stays readable for
// whoever handles the error next. Platforms without it (jsdom) fall back to a
// FileReader.
const readBlobText = (blob: Blob): Promise<string> => {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

/**
 * Same check as `isMiddlewareAuthRejection` for an error body that arrived as a
 * Blob or ArrayBuffer (a request made with `responseType: 'blob'` or
 * `'arraybuffer'`). Decodes the bytes as UTF-8 JSON and compares the `detail`.
 * Resolves false for anything else: a non-binary body, bytes that are not JSON,
 * or a platform that cannot read them. It never throws, and it leaves the body
 * itself untouched.
 */
export const isMiddlewareAuthRejectionBinaryBody = async (data: unknown): Promise<boolean> => {
  try {
    let text: string;
    if (isBlob(data)) text = await readBlobText(data);
    else if (isArrayBuffer(data)) text = new TextDecoder().decode(data);
    else return false;
    return hasAuthRejectedDetail(JSON.parse(text));
  } catch {
    return false;
  }
};

/**
 * True when the failed request carried a Bearer token, so the API's rejection
 * of it means the session token is stale and the interceptor's one silent
 * refresh-and-retry had a chance to recover it. Reads `error.config.headers`,
 * which is an AxiosHeaders instance for requests that went through axios and may
 * be a plain object elsewhere.
 */
export const requestSentBearerToken = (error: unknown): boolean => {
  if (error == null || typeof error !== 'object') return false;
  const config = (error as { config?: unknown }).config;
  if (config == null || typeof config !== 'object') return false;
  const headers = (config as { headers?: unknown }).headers;
  if (headers == null || typeof headers !== 'object') return false;

  const get = (headers as { get?: unknown }).get;
  let value: unknown = typeof get === 'function' ? get.call(headers, 'Authorization') : undefined;
  if (value == null) {
    const record = headers as Record<string, unknown>;
    value = record.Authorization ?? record.authorization;
  }
  return typeof value === 'string' && value.startsWith('Bearer ');
};
